import React from "react";
import classNames from "classnames";
import Select, {components} from "react-select";
import Creatable from "react-select/lib/Creatable";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import MenuItem from "@material-ui/core/MenuItem";
import CancelIcon from "@material-ui/icons/Cancel";
import { emphasize } from "@material-ui/core/styles/colorManipulator";
import { isEqual } from "lodash/lang";

const styles = (theme) => ({
  root: {
    flexGrow: 1,
    marginTop: 16,
    marginBottom: 8,
    display: "inline-flex",
    width: "100%",
    "&>div": {
      width: "inherit",
    },
    "&> svg": {
      cursor: "pointer",
    },
  },
  input: {
    display: "flex",
    padding: "8px 0px",
  },
  valueContainer: {
    display: "flex",
    flexWrap: "wrap",
    flex: 1,
    alignItems: "center",
    overflow: "hidden",
    padding: "0px 0px 0px 14px",
    maxHeight: 150,
    overflowY: "auto",
    caretColor: theme.TextColor,
    fontSize: theme.textFontSize,
    wordBreak: 'break-all'
  },
  chip: {
    margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
    height: 24,
    color: theme.TextColor,
    backgroundColor: theme.AppBGColor,
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === "light"
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
      0.08
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
    color: theme.iconColor,
  },
  singleValue: {
    fontSize: theme.textFontSize,
    color: theme.TextColor,
  },
  placeholder: {
    left: 2,
    fontSize: theme.textFontSize,
  },
  menuList: {
    maxHeight: 200,
    overflowY: "auto",
    paddingBottom: 4,
    paddingTop: 4,
    "-webkit-overflow-scrolling": "touch",
    boxSizing: "border-box",
    position: "relative",
    boxShadow: `0px 2px 4px ${theme.AppBGColor}`
  },
  menuItem: {
    fontSize: theme.textFontSize,
    padding: "8px 14px",
    minHeight: "30px",
    color: theme.TextColor,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  menuItemDisabled:{
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  divider: {
    height: theme.spacing.unit * 2,
  },
  label: {
    fontSize: 14,
    zIndex: 0,
    transform: `translate(14px, 14px) scale(1)`,
    color: theme.TextColor,
    opacity: 0.7,
    "&$disabledLabel": {
      zIndex:10,
      color: theme.iconColor,
    },
    "&$focusedLabel": {
      color: theme.palette.primary.main,
      opacity: 1
    },
  },
  warningLabel: {
    color: theme.TextColor,
    transform: "translate(14px, 14px) scale(1)",
    fontSize: theme.headerTextFontSize,
    opacity: 0.7,
    "&$focusedLabel": {
      color: "#F3C03E",
    },
  },
  disabledLabel:{},
  focusedLabel: {},
  notchedOutline: {},
  textField: {},
  inputRoot: {
    "& $notchedOutline,$inputMultiline": {
      padding: 0,
    },
    "&$inputDisabled": {
      padding: 0,
    },
    "& $notchedOutline, &:hover:not($inputFocused):not($inputDisabled):not($inputError) $notchedOutline":
      {
        borderColor: `${theme.iconColor}`,
    },
    "&$inputFocused $notchedOutline":
    {
      borderWidth: 1,
      borderColor: theme.palette.primary.main,
    },
      "&$inputDisabled $singleValue": {
        color: theme.iconColor,
    },
  },
  inputRootWarning: {
    "& $notchedOutline, &:hover:not($inputFocused):not($inputDisabled):not($inputError) $notchedOutline":
      {
        borderColor: "#F3C03E",
      },
    "& $notchedOutline, &$inputFocused $notchedOutline": {
      borderColor: "#F3C03E",
    },
  },
  inputField: {},
  inputFocused: {},
  inputMultiline:{},
  inputDisabled: { 
    background: theme.tooltipBGColor,
    "& $notchedOutline, &:not($inputFocused):not($inputError) $notchedOutline":
      {
        borderColor: theme.iconColor,
      },
  },
  inputError: {},
  chipIcon: {
    color: theme.iconColor,
    cursor: "pointer",
    "&:hover": {
      color: theme.iconColor,
    },
  },
  icon: {
    color: theme.iconColor,
    cursor: "pointer",
    "&:hover": {
      color: theme.iconColor,
    },
  },
  clearIndicator: {
    padding: "0px 0px 0px 5px",
    color: theme.iconColor,
    cursor: "pointer",
  },
  disabledSelect: {
    cursor: "not-allowed", 
  },
  labelShrink:{}
});

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

function Control(props) {
  const { classes, showWarning, InputPropsClasses, textFieldProps = {} } = props.selectProps;
  const isOutlined = textFieldProps.variant === "outlined";

  const inputClasses = {
    root: showWarning ? classes.inputRootWarning : classes.inputRoot,
    input: classes.inputField,
    focused: classes.inputFocused,
    disabled: classes.inputDisabled,
    error: classes.inputError,
    ...InputPropsClasses
  };

  if (isOutlined) {
    inputClasses.notchedOutline = classes.notchedOutline;
  }

  return (
    <TextField
      fullWidth
      disabled={props.isDisabled}
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
        classes: inputClasses,
      }}
      {...textFieldProps}
    />
  );
}

function Option(props) {
  const { classes, rmaxTheme: theme } = props.selectProps;
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
        height: 'auto',
        backgroundColor: props.isFocused ? theme.customMultiSelectColor : 'inherit', //hover style
      }}
      classes={{
        root: `${classes.menuItem} ${props.isDisabled ? classes.menuItemDisabled : null}`,
      }}
      {...props.innerProps}
    >
     {props.children}{props.data.labelSuffixNewLine ? <><br/>{props.data.labelSuffix}</>: props.data.labelSuffix || ""}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <Typography
      color={props.error ? "error" : "textSecondary"}
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function SingleValue(props) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  return (
    <div className={`${props.selectProps.classes.valueContainer} customScroll`}>
      {props.children}
    </div>
  );
}

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={classNames(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={
        <CancelIcon
          {...props.removeProps}
          classes={{ root: props.selectProps.classes.chipIcon }}
        />
      }
    />
  );
}

function Menu(props) {
  return (
    <components.Menu {...props}>
      {props.children}
    </components.Menu>
  );
}

function MenuList(props) {
  return (
    <div
      ref={props.innerRef}
      className={`${props.selectProps.classes.menuList} customScroll`}
    >
      {props.children}
    </div>
  );
}

function ClearIndicator(props) {
  const {
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div {...restInnerProps} ref={ref}>
      <div className={props.selectProps.classes.clearIndicator}>
        <CancelIcon classes={{ root: props.selectProps.classes.icon }} />
      </div>
    </div>
  );
}

const customComponents = {
  Control,
  Menu,
  MenuList,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  ClearIndicator,
};

class StyledSelect extends React.Component {
  state = {
    value: null,
    inputValue: "",
  };
  inputRef = React.createRef();

  componentDidMount() {
    this.updateValues();
    if (this.props.autoFocus) {
      setTimeout(() => {
        this.inputRef?.current?.focus();
      });
    }
    this.props.getInputRef?.(this.inputRef);
  }

  componentDidUpdate(prevProps) {
    if (
      !isEqual(prevProps.data, this.props.data) ||
      !isEqual(prevProps.value, this.props.value)
    ) {
      this.updateValues();
    }
  }

  updateValues() {
    let value = null;
    if (this.props.value) {
      if (Array.isArray(this.props.value)) {
        value = this.props.data.filter(
          (option) =>
            this.props.value.includes(option.label) ||
            this.props.value.includes(option.value)
        );
      } else {
        value = this.props.data.find(
          (option) =>
            option.label === this.props.value ||
            option.value === this.props.value
        );
      }
    }
    if (value !== this.state.value) {
      this.handleChange(value);
    }
  }

  isValidNewOption = (inputValue, selectValue, selectOptions) => {
    if (this.props.creatableIsValidNewOption) {
      return this.props.creatableIsValidNewOption({
        inputValue,
        selectValue,
        selectOptions,
      });
    }
    const format = this.props.newValueParams?.format;
    return format
      ? inputValue &&
          selectOptions.findIndex((option) => [option.label, option.value].includes(inputValue)) <
            0 &&
          format.test(inputValue)
      : true;
  };

  noOptionsMessage = () => {
    if (this.props.canAcceptNewValue) {
      return `${this.props.newValueParams?.placeholder}`;
    }
    return "No options";
  };

  onInputChange = (value) => {
    const capitalize = this.props?.newValueParams?.capitalize ?? false;
    this.setState({
      inputValue: value && capitalize ? value.toUpperCase() : value,
    });
  };

  handleChange = (value) => {
    this.setState({
      value,
    });
    this.props.onChange?.(value);
  };

  formatCreateLabel = (inputText) => {
    return `${this.props.createLabel || "Create"} "${inputText}"`;
  };

  filterOption = (option, filterValue) => {
    const searchParamsList = [option.label, option?.value, option.data?.labelSuffix].filter(Boolean)
    return searchParamsList.some((params) =>
      params.toString().toUpperCase().includes(filterValue.toString().toUpperCase())
    );
}

  render() {
    const { classes, theme, rootClass } = this.props;

    const selectStyles = {
      input: (base) => ({
        ...base,
        color: theme.TextColor,
        "& input": {
          font: "inherit",
        },
      }),
      dropdownIndicator: (base) => ({
        ...base,
        color: theme.iconColor,
        cursor: "pointer",
        padding: "0px 8px",
        ":hover": {
          color: theme.iconColor,
        },
      }),
      indicatorSeparator: (base) => ({
        ...base,
        marginTop: 0,
        marginBottom: 0,
        backgroundColor: theme.iconColor,
        display: "none",
      }),
      menu: styles => ({
        ...styles,
        margin: 0,
        borderRadius: 0,
        backgroundColor: theme.ModalBGColor,
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 })
    };
    const Dropdown = this.props.canAcceptNewValue ? Creatable : Select;
    return (
      <div
        className={`${classes.root} ${rootClass} ${
          this.props.isDisabled ? classes.disabledSelect : ''
        }`}>
        <Dropdown
          classes={classes}
          styles={selectStyles}
          textFieldProps={{
            label: "Select",
            InputLabelProps: {
              classes: {
                root: this.props.showWarning
                  ? classes.warningLabel
                  : classes.label,
                focused: classes.focusedLabel,
                disabled: classes.disabledLabel,
                shrink: classes.labelShrink,
              },
            },
            value: this.props.value || "",
            ...this.props.textFieldProps,
            error: this.props.error,
          }}
          InputPropsClasses={this.props.InputPropsClasses}
          menuPosition={this.props.menuPosition || "absolute"}
          menuPlacement={this.props.menuPlacement || "auto"}
          menuShouldScrollIntoView={false}
          menuPortalTarget={document.body}
          showWarning={this.props.showWarning}
          options={this.props.data}
          components={customComponents}
          value={this.state.value}
          onChange={this.handleChange}
          isDisabled={this.props.isDisabled}
          placeholder={""}
          isMulti={this.props.isMulti}
          closeMenuOnSelect={!this.props.isMulti}
          isClearable={!this.props.isNotClearable}
          inputValue={this.state.inputValue}
          onInputChange={this.onInputChange}
          filterOption={this.filterOption}
          isValidNewOption={this.isValidNewOption}
          noOptionsMessage={this.noOptionsMessage}
          formatCreateLabel={this.formatCreateLabel}
          rmaxTheme={theme} //The react-select component accepts a theme prop for styling, which we've renamed to rmaxTheme to enable manual customization of components if needed.
          ref={this.inputRef}
        />
      </div>
    );
  }
}
export default withStyles(styles, { withTheme: true })(StyledSelect);
